import React, { useEffect, useRef, useState } from 'react'
import {
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCcw,
  Trash2,
  X,
  ZoomIn,
} from 'lucide-react'
import {
  AddFileBtn,
  ChecklistRowBody,
  ChecklistRowDescription,
  ChecklistRowDescriptionText,
  ChecklistRowHeader,
  ChecklistRowIndex,
  ChecklistRowMeta,
  ChecklistRowMetaLabel,
  ChecklistRowMetaValue,
  ChecklistRow as ChecklistRowStyle,
  CommentBubble,
  CommentContent,
  CommentMeta,
  CommentsBlock,
  CommentsHeader,
  DangerButton,
  FileNonImage,
  FileThumb,
  FilesGrid,
  ItemStatusBadge,
  KebabButton,
  KebabMenu,
  KebabMenuItem,
  KebabSeparator,
  KebabWrapper,
  NewCommentInput,
  NewCommentRow,
  PrimaryButton,
  RemoveFileBtn,
  TextArea,
  VoceTag,
} from './styles'
import { ChecklistItem } from './types'
import { ApprovalApi } from './api'
import ImageLightbox from '../../../../components/ImageViewer'
import AttachmentSourceModal from '../../../../components/AttachmentSourceModal'
import AttachmentGalleryModal, { GalleryFile } from '../../../../components/AttachmentGalleryModal'
import UserAvatar from '../../../../components/UserAvatar'
import AttachmentApiService from '../../../../services/AttachmentApiService'
import openSignedAttachment from '../../../../utils/openSignedAttachment'

interface Props {
  item: ChecklistItem
  index: number
  isAdmin: boolean
  currentUserId?: number
  locked: boolean
  onItemChanged: (item: ChecklistItem) => void
  onItemRemoved: (id: number) => void
}

const isImageMime = (mime?: string | null) =>
  !!mime && mime.toLowerCase().startsWith('image/')

const formatDateTime = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const initials = (name?: string | null) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2)
  return (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')
}

const ChecklistItemRow: React.FC<Props> = ({
  item,
  index,
  isAdmin,
  currentUserId,
  locked,
  onItemChanged,
  onItemRemoved,
}) => {
  const [isOpen, setIsOpen] = useState(item.status === 'PENDING')
  const [editingDescription, setEditingDescription] = useState(false)
  const [descriptionDraft, setDescriptionDraft] = useState(item.description)
  const [commentDraft, setCommentDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false)
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false)
  const isChoosingRef = useRef(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const [signedImageUrls, setSignedImageUrls] = useState<Record<number, string>>({})

  useEffect(() => {
    let active = true
    const toSign = item.Files.filter((f) => isImageMime(f.GeneralFile?.mimeType) && f.GeneralFile?.fileUrl)
    if (!toSign.length) return

    Promise.all(
      toSign.map(async (f) => {
        const rawUrl = f.GeneralFile.fileUrl
        if (rawUrl.includes('X-Goog-Signature')) return { id: f.id, url: rawUrl }
        try {
          const signed = await AttachmentApiService.getSignedUrl(rawUrl)
          return { id: f.id, url: signed || rawUrl }
        } catch {
          return { id: f.id, url: rawUrl }
        }
      }),
    ).then((results) => {
      if (!active) return
      const map: Record<number, string> = {}
      results.forEach((r) => {
        map[r.id] = r.url
      })
      setSignedImageUrls(map)
    })

    return () => {
      active = false
    }
  }, [item.Files])

  const imageFiles = item.Files.filter((f) => isImageMime(f.GeneralFile?.mimeType))
  const lightboxSlides = imageFiles.map((f) => ({
    src: signedImageUrls[f.id] || f.GeneralFile.fileUrl,
    title: f.GeneralFile.title || f.GeneralFile.originalName || 'Anexo',
  }))

  const openLightboxForFile = (fileInItemId: number) => {
    const targetIndex = imageFiles.findIndex((f) => f.id === fileInItemId)
    setLightboxIndex(Math.max(0, targetIndex))
    setLightboxOpen(true)
  }

  const readOnly = locked
  const canDecide = isAdmin && !readOnly

  useEffect(() => {
    if (!menuOpen) return
    const handleDocClick = (e: MouseEvent) => {
      if (!menuRef.current) return
      if (!menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleDocClick)
    return () => document.removeEventListener('mousedown', handleDocClick)
  }, [menuOpen])

  const handleDecide = async (status: 'APPROVED' | 'REJECTED') => {
    if (!canDecide || busy) return
    setBusy(true)
    try {
      const updated = await ApprovalApi.decideChecklistItem(item.id, status)
      onItemChanged(updated)
    } finally {
      setBusy(false)
    }
  }

  const handleResetDecision = async () => {
    if (!canDecide || busy) return
    setBusy(true)
    try {
      const updated = await ApprovalApi.decideChecklistItem(item.id, 'PENDING')
      onItemChanged(updated)
    } finally {
      setBusy(false)
    }
  }

  const handleSaveDescription = async () => {
    if (readOnly || !descriptionDraft.trim() || descriptionDraft === item.description) {
      setEditingDescription(false)
      return
    }
    setBusy(true)
    try {
      const updated = await ApprovalApi.updateChecklistItem(item.id, {
        description: descriptionDraft.trim(),
      })
      onItemChanged({ ...updated, Comments: item.Comments, Files: item.Files })
    } finally {
      setBusy(false)
      setEditingDescription(false)
    }
  }

  const handleRemove = async () => {
    if (readOnly || busy) return
    if (!window.confirm('Remover este item do checklist?')) return
    setBusy(true)
    try {
      await ApprovalApi.removeChecklistItem(item.id)
      onItemRemoved(item.id)
    } finally {
      setBusy(false)
    }
  }

  const handleOpenSourceModal = () => {
    if (readOnly || busy) return
    isChoosingRef.current = false
    setIsSourceModalOpen(true)
  }

  const handleChooseUpload = () => {
    isChoosingRef.current = true
    fileInputRef.current?.click()
  }

  const handleChooseGallery = () => {
    isChoosingRef.current = true
    setIsGalleryModalOpen(true)
  }

  const handleCloseSourceModal = () => {
    setIsSourceModalOpen(false)
    isChoosingRef.current = false
  }

  const handleCloseGalleryModal = () => {
    setIsGalleryModalOpen(false)
  }

  const handleSelectGalleryFiles = async (selectedFiles: GalleryFile[]) => {
    if (!selectedFiles?.length || readOnly || busy) return
    setBusy(true)
    try {
      const fileIds = selectedFiles.map((f) => f.id)
      const createdList = await ApprovalApi.attachChecklistFiles(item.id, fileIds)
      onItemChanged({ ...item, Files: [...item.Files, ...createdList] })
    } finally {
      setBusy(false)
      setIsGalleryModalOpen(false)
    }
  }

  const handleAddFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || readOnly) return
    setBusy(true)
    try {
      const created = await ApprovalApi.addChecklistFile(item.id, file)
      onItemChanged({ ...item, Files: [...item.Files, created] })
    } finally {
      setBusy(false)
    }
  }

  const handleRemoveFile = async (fileInItemId: number) => {
    if (readOnly || busy) return
    setBusy(true)
    try {
      await ApprovalApi.removeChecklistFile(fileInItemId)
      onItemChanged({
        ...item,
        Files: item.Files.filter((f) => f.id !== fileInItemId),
      })
    } finally {
      setBusy(false)
    }
  }

  const handleAddComment = async () => {
    const trimmed = commentDraft.trim()
    if (!trimmed || readOnly || busy) return
    setBusy(true)
    try {
      const created = await ApprovalApi.addChecklistComment(item.id, trimmed)
      onItemChanged({ ...item, Comments: [...item.Comments, created] })
      setCommentDraft('')
    } finally {
      setBusy(false)
    }
  }

  const [editingCommentId, setEditingCommentId] = useState<number | null>(null)
  const [editingCommentDraft, setEditingCommentDraft] = useState('')

  const startEditComment = (commentId: number, currentText: string) => {
    setEditingCommentId(commentId)
    setEditingCommentDraft(currentText)
  }

  const cancelEditComment = () => {
    setEditingCommentId(null)
    setEditingCommentDraft('')
  }

  const handleSaveEditedComment = async (commentId: number) => {
    const trimmed = editingCommentDraft.trim()
    if (!trimmed || busy) return
    setBusy(true)
    try {
      const updated = await ApprovalApi.updateChecklistComment(commentId, trimmed)
      onItemChanged({
        ...item,
        Comments: item.Comments.map((c) => (c.id === commentId ? { ...c, comment: updated.comment } : c)),
      })
      setEditingCommentId(null)
      setEditingCommentDraft('')
    } catch (error) {
      console.error('Erro ao atualizar comentário:', error)
    } finally {
      setBusy(false)
    }
  }

  const handleRemoveComment = async (commentId: number) => {
    if (busy) return
    setBusy(true)
    try {
      await ApprovalApi.removeChecklistComment(commentId)
      onItemChanged({
        ...item,
        Comments: item.Comments.filter((c) => c.id !== commentId),
      })
    } finally {
      setBusy(false)
    }
  }

  const approver = item.Approver
  const approverIsCurrentUser =
    !!currentUserId && approver?.id === currentUserId

  const openEditFromMenu = () => {
    setIsOpen(true)
    setDescriptionDraft(item.description)
    setEditingDescription(true)
    setMenuOpen(false)
  }

  const removeFromMenu = () => {
    setMenuOpen(false)
    handleRemove()
  }

  return (
    <ChecklistRowStyle>
      <ChecklistRowHeader $clickable onClick={() => setIsOpen((p) => !p)}>
        <ChecklistRowIndex>{String(index + 1).padStart(2, '0')}</ChecklistRowIndex>
        <ChecklistRowDescription>
          <ChecklistRowDescriptionText>{item.description}</ChecklistRowDescriptionText>
          <ItemStatusBadge $status={item.status}>
            {item.status === 'PENDING' ? '—' : item.status === 'APPROVED' ? 'Aprovado' : 'Reprovado'}
          </ItemStatusBadge>
        </ChecklistRowDescription>
        <ChecklistRowMeta>
          <ChecklistRowMetaLabel>Aprovador</ChecklistRowMetaLabel>
          <ChecklistRowMetaValue>
            {approver?.name ?? '—'}
            {approverIsCurrentUser && <VoceTag>VOCÊ</VoceTag>}
          </ChecklistRowMetaValue>
        </ChecklistRowMeta>
        {!readOnly && (
          <KebabWrapper ref={menuRef} onClick={(e) => e.stopPropagation()}>
            <KebabButton
              type='button'
              onClick={() => setMenuOpen((p) => !p)}
              title='Mais opções'
            >
              <MoreVertical size={16} />
            </KebabButton>
            {menuOpen && (
              <KebabMenu>
                <KebabMenuItem
                  type='button'
                  onClick={() => {
                    setMenuOpen(false)
                    handleDecide('APPROVED')
                  }}
                  disabled={!canDecide || busy || item.status === 'APPROVED'}
                >
                  <Check size={14} /> Aprovar este item
                </KebabMenuItem>
                <KebabMenuItem
                  type='button'
                  onClick={() => {
                    setMenuOpen(false)
                    handleDecide('REJECTED')
                  }}
                  disabled={!canDecide || busy || item.status === 'REJECTED'}
                >
                  <X size={14} /> Reprovar este item
                </KebabMenuItem>
                <KebabMenuItem
                  type='button'
                  onClick={() => {
                    setMenuOpen(false)
                    handleResetDecision()
                  }}
                  disabled={!canDecide || busy || item.status === 'PENDING'}
                >
                  <RefreshCcw size={14} /> Voltar para pendente
                </KebabMenuItem>
                <KebabSeparator />
                <KebabMenuItem
                  type='button'
                  onClick={openEditFromMenu}
                  disabled={busy}
                >
                  <Pencil size={14} /> Editar descrição
                </KebabMenuItem>
                <KebabMenuItem
                  type='button'
                  $danger
                  onClick={removeFromMenu}
                  disabled={busy}
                >
                  <Trash2 size={14} /> Remover item
                </KebabMenuItem>
              </KebabMenu>
            )}
          </KebabWrapper>
        )}
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </ChecklistRowHeader>

      {isOpen && (
        <ChecklistRowBody>
          {editingDescription && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <TextArea
                value={descriptionDraft}
                onChange={(e) => setDescriptionDraft(e.target.value)}
                disabled={readOnly}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <PrimaryButton type='button' onClick={handleSaveDescription} disabled={busy}>
                  Salvar
                </PrimaryButton>
                <DangerButton
                  type='button'
                  onClick={() => {
                    setDescriptionDraft(item.description)
                    setEditingDescription(false)
                  }}
                >
                  Cancelar
                </DangerButton>
              </div>
            </div>
          )}

          <FilesGrid>
            {item.Files.map((f) => {
              const file = f.GeneralFile
              if (isImageMime(file.mimeType)) {
                return (
                  <FileThumb key={f.id}>
                    <img
                      src={signedImageUrls[f.id] || file.fileUrl}
                      alt={file.title ?? 'anexo'}
                      style={{ cursor: 'pointer', width: '100%', height: '100%', objectFit: 'cover' }}
                      onClick={() => openLightboxForFile(f.id)}
                      onError={async (e) => {
                        const target = e.target as HTMLImageElement
                        if (!target.dataset.retried && file.fileUrl) {
                          target.dataset.retried = 'true'
                          try {
                            const signed = await AttachmentApiService.getSignedUrl(file.fileUrl)
                            if (signed && signed !== file.fileUrl) {
                              target.src = signed
                            }
                          } catch {
                            target.dataset.retried = 'failed'
                          }
                        }
                      }}
                    />
                    <div className='zoom-overlay'>
                      <ZoomIn size={16} />
                      <span>Aumentar</span>
                    </div>
                    {!readOnly && (
                      <RemoveFileBtn
                        type='button'
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemoveFile(f.id)
                        }}
                        title='Remover anexo'
                      >
                        <X size={10} />
                      </RemoveFileBtn>
                    )}
                  </FileThumb>
                )
              }
              return (
                <FileNonImage
                  key={f.id}
                  title={file.originalName ?? file.title ?? ''}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (file.fileUrl) {
                      void openSignedAttachment(file.fileUrl)
                    }
                  }}
                >
                  <FileText size={20} />
                  <span>{(file.originalName ?? file.title ?? 'arquivo').slice(0, 20)}</span>
                  {!readOnly && (
                    <RemoveFileBtn
                      type='button'
                      onClick={() => handleRemoveFile(f.id)}
                      title='Remover anexo'
                    >
                      <X size={10} />
                    </RemoveFileBtn>
                  )}
                </FileNonImage>
              )
            })}
            {!readOnly && (
              <AddFileBtn
                onClick={handleOpenSourceModal}
                title='Adicionar anexo'
              >
                <Plus size={22} />
              </AddFileBtn>
            )}
            <input
              ref={fileInputRef}
              type='file'
              style={{ display: 'none' }}
              onChange={handleAddFile}
            />
          </FilesGrid>

          <CommentsBlock>
            <CommentsHeader>Comentários</CommentsHeader>
            {item.Comments.map((c) => {
              const canModify = !readOnly && (c.authorId === currentUserId || isAdmin)
              const isEditingThisComment = editingCommentId === c.id

              return (
                <CommentBubble key={c.id}>
                  <UserAvatar
                    src={(c.Author as any)?.picture}
                    name={c.Author?.name}
                    size={28}
                    fontSize="11px"
                  />
                  <CommentContent>
                    <CommentMeta>
                      <strong>{c.Author?.name ?? 'Usuário'}</strong>
                      <span>{formatDateTime(c.createdAt)}</span>
                      {canModify && !isEditingThisComment && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                          <button
                            type='button'
                            onClick={() => startEditComment(c.id, c.comment)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#9ca3af',
                              cursor: 'pointer',
                              padding: 2,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            title='Editar comentário'
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            type='button'
                            onClick={() => handleRemoveComment(c.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#9ca3af',
                              cursor: 'pointer',
                              padding: 2,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            title='Remover comentário'
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                    </CommentMeta>
                    {isEditingThisComment ? (
                      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <NewCommentInput
                          value={editingCommentDraft}
                          onChange={(e) => setEditingCommentDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              handleSaveEditedComment(c.id)
                            }
                            if (e.key === 'Escape') {
                              cancelEditComment()
                            }
                          }}
                          disabled={busy}
                          autoFocus
                          style={{ width: '100%' }}
                        />
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            type='button'
                            onClick={cancelEditComment}
                            disabled={busy}
                            style={{
                              padding: '4px 10px',
                              fontSize: '12px',
                              border: '1px solid #d1d5db',
                              background: '#fff',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              color: '#4b5563',
                            }}
                          >
                            Cancelar
                          </button>
                          <PrimaryButton
                            type='button'
                            onClick={() => handleSaveEditedComment(c.id)}
                            disabled={busy || !editingCommentDraft.trim()}
                            style={{ padding: '4px 12px', fontSize: '12px' }}
                          >
                            Salvar
                          </PrimaryButton>
                        </div>
                      </div>
                    ) : (
                      <span>{c.comment}</span>
                    )}
                  </CommentContent>
                </CommentBubble>
              )
            })}
            {!readOnly && (
              <NewCommentRow>
                <NewCommentInput
                  placeholder='Escreva um comentário...'
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddComment()
                    }
                  }}
                  disabled={busy}
                />
                <PrimaryButton
                  type='button'
                  onClick={handleAddComment}
                  disabled={busy || !commentDraft.trim()}
                >
                  Enviar
                </PrimaryButton>
              </NewCommentRow>
            )}
          </CommentsBlock>
        </ChecklistRowBody>
      )}

      <ImageLightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxSlides}
        index={lightboxIndex}
      />

      <AttachmentSourceModal
        isOpen={isSourceModalOpen}
        onClose={handleCloseSourceModal}
        title={`Anexo - Checklist #${String(index + 1).padStart(2, '0')}`}
        description='Escolha como deseja adicionar o anexo a este item do checklist:'
        uploadLabel='Upload de arquivo'
        uploadDescription='Selecione um arquivo do seu dispositivo'
        galleryLabel='Selecionar da galeria'
        galleryDescription='Escolha anexos salvos na empresa'
        onChooseUpload={handleChooseUpload}
        onChooseGallery={handleChooseGallery}
      />

      <AttachmentGalleryModal
        isOpen={isGalleryModalOpen}
        onClose={handleCloseGalleryModal}
        multiple={true}
        defaultFilter='all'
        title={`Galeria da Empresa - Item #${String(index + 1).padStart(2, '0')}`}
        onSelect={handleSelectGalleryFiles}
      />
    </ChecklistRowStyle>
  )
}

export default ChecklistItemRow
